import SLSRepresentanteService from '@/services/Cadastros/ApoioVendas/SLSRepresentante.service';
import SLSClienteService from '@/services/Cadastros/Cliente/SLSCliente.service';
import SLSCondicaoPagamentoService from '@/services/SistemaDKP/SLSCondicaoPagamento.service';
import SISUsuarioCliente from '@/services/SistemaDKP/SISUsuarioCliente.service';
import store from '@/store';
import { isPerfilClienteAtivo, isPerfilRepresentanteAtivo } from '@/libs/perfilCliente';

const slsRepresentanteService = new SLSRepresentanteService();
const slsClienteService = new SLSClienteService();
const slsCondicaoPagamentoService = new SLSCondicaoPagamentoService();
const sisUsuarioCliente = new SISUsuarioCliente();

export default class InitialDataService {
  static getUserId() {
    try {
      // O localStorage 'userData' tem DOIS formatos conforme o momento:
      // plano ({ id, ... }) logo após o login e embrulhado ({ userData: {...} })
      // depois do auth/SET_USER. Aceitar ambos — senão o login busca/salva
      // com usuário 'default' e o cache do Info Pedido fica com chave errada.
      const userData = JSON.parse(localStorage.getItem('userData'));
      return userData?.id || userData?.userData?.id || 'default';
    } catch (error) {
      console.error('Erro ao obter ID do usuário:', error);
      return 'default';
    }
  }

  static getSessionId() {
    try {
      return localStorage.getItem('sessionId') || null;
    } catch (error) {
      console.error('Erro ao obter ID da sessão:', error);
      return null;
    }
  }

  static generateSessionId() {
    const sessionId = Date.now().toString();
    localStorage.setItem('sessionId', sessionId);
    return sessionId;
  }

  static isValidCache(cacheData) {
    try {
      if (!cacheData || typeof cacheData !== 'object') return false;

      const currentSessionId = this.getSessionId();
      if (!currentSessionId || !cacheData.sessionId) return false;

      return cacheData.sessionId === currentSessionId;
    } catch (error) {
      console.error('Erro ao validar cache:', error);
      return false;
    }
  }

  static clearOldCaches() {
    try {
      const allKeys = Object.keys(localStorage);
      const infoPedidoKeys = allKeys.filter((key) => key.startsWith('infoPedidoForm_'));

      infoPedidoKeys.forEach((key) => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (!this.isValidCache(data)) {
            localStorage.removeItem(key);
            console.log(`Cache antigo removido: ${key}`);
          }
        } catch (error) {
          // Se não conseguir parsear, remove o cache corrompido
          localStorage.removeItem(key);
          console.log(`Cache corrompido removido: ${key}`);
        }
      });
    } catch (error) {
      console.error('Erro ao limpar caches antigos:', error);
    }
  }

  static async loadInitialData() {
    try {
      // Gerar novo sessionId para esta sessão
      this.generateSessionId();

      // Limpar caches de sessões antigas
      this.clearOldCaches();

      await store.dispatch('InfoPedido/resertInfoPedido');
      const isRepresentante = isPerfilRepresentanteAtivo();
      // Auto-preenchimento do CLIENTE só vale para o perfil de cliente final
      // ('Cliente' ou 'ClienteFinal'). Representante recebe só o representante
      // (quando tem exatamente 1); Supervisor e demais perfis começam vazios e
      // o usuário preenche pelo Info Pedido.
      const isClienteFinal = isPerfilClienteAtivo();

      // Se for Representante, carregar o representante associado ao usuário e fixá-lo
      if (isRepresentante) {
        const representanteData = await this.loadRepresentanteForRepresentantePerfil();
        if (representanteData) {
          localStorage.setItem('representanteData', JSON.stringify(representanteData));
          console.log('Dados do representante salvos no localStorage');
        }
      }

      if (isClienteFinal) {
        const clienteData = await this.loadClienteFinalData();
        if (clienteData) {
          localStorage.setItem('clienteData', JSON.stringify({
            cliente: clienteData.cliente,
            representante: clienteData.representante,
            detalhes: clienteData.clienteInfo,
          }));
        }
      }

      return true;
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      return false;
    }
  }

  static async loadClienteFinalData() {
    try {
      // 1. Buscar o cliente associado ao usuário logado
      await store.dispatch('SLSCliente/resertFilters');
      const clientes = await sisUsuarioCliente.listarClienteSelecionados({
        usuarioId: this.getUserId(),
      });

      if (!clientes || clientes.length === 0) {
        console.error('Nenhum cliente encontrado para o usuário com perfil ClienteFinal');
        return null;
      }

      // Pegar o primeiro cliente encontrado (geralmente só deve haver um)
      const cliente = clientes[0];
      console.log('Cliente encontrado:', cliente);

      // 2. Buscar informações detalhadas do cliente diretamente. O cliente ativo
      // é determinado pelo vínculo usuario-cliente acima; este fluxo não consulta
      // a carteira de representante (essa rota continua exclusiva aos demais perfis).
      // Não-fatal: a IDENTIDADE do cliente (clienteid/nome) já veio de listarClienteSelecionados
      // e não depende de endereço de entrega / condição de pagamento. Se o enriquecimento falhar
      // (ex.: cliente sem endereço de entrega -> ERRO_CLIENTE_ENDERECO_ENTREGA_NOT_FOUND), seguimos
      // apenas com a identidade para que o cliente continue sendo identificado (ex.: no dashboard).
      let clienteInfo = null;
      try {
        clienteInfo = await slsClienteService.getInfoPedidoByClienteId(cliente.clienteiderp);
      } catch (infoError) {
        console.warn(
          'Não foi possível obter informações detalhadas do cliente; seguindo apenas com a identidade:',
          (infoError && infoError.message) || infoError,
        );
      }

      // 3. Montar o objeto completo com todos os dados. Quando o próprio payload
      // de Info Pedido trouxer o representante comercial, ele pode ser exibido e
      // enviado no pedido sem uma segunda chamada à carteira.
      const clienteEnriquecido = (clienteInfo && clienteInfo.cliente) || {};
      const representante = (clienteInfo && clienteInfo.representante)
        || cliente.representante
        || ((clienteEnriquecido.representanteid || cliente.representanteid
          || clienteEnriquecido.representantenome || cliente.representantenome) ? {
          representanteid: clienteEnriquecido.representanteid || cliente.representanteid || '',
          representantenome: clienteEnriquecido.representantenome || cliente.representantenome || '',
        } : null);
      // SET_FORM_INFO_PEDIDO substitui o formulário inteiro pelo payload, então garantimos
      // uma SHAPE consistente dos blocos aninhados — quando o enriquecimento (clienteInfo) falhou,
      // estes viriam ausentes e quebrariam consumidores que leem propriedades aninhadas.
      const formData = {
        ...(clienteInfo || {}),
        // Mescla o cliente COMPLETO do enriquecimento (canalvendaid, clientenomeabreviado,
        // clienteisentoie etc.) com a identidade mínima de listarClienteSelecionados —
        // substituir pelo objeto mínimo perdia campos usados pela simulação de imposto.
        cliente: { ...((clienteInfo && clienteInfo.cliente) || {}), ...cliente },
        representante: representante || {
          representanteid: '',
          representantenome: '',
        },
      };
      formData.condicaoPagamento = formData.condicaoPagamento || { condicaopagamentoid: null, condicaopagamentodescricao: null };
      formData.tabelaPreco = formData.tabelaPreco || { tabelapreco_id: null, tabelapreco_descricao: '' };
      formData.estabelecimento = formData.estabelecimento || { estabelecimentoid: '', estabelecimentorazaosocial: '' };
      formData.tipoPedidoCliente = formData.tipoPedidoCliente || { tipopedidoid: null, tipopedidodescricao: null };
      if (typeof formData.enderecoEntrega === 'undefined') formData.enderecoEntrega = null;

      // Verificar se a tabela de preço principal do cliente está nas opções disponíveis
      if (clienteInfo && clienteInfo.cliente && clienteInfo.cliente.tabelaprecoid
        && clienteInfo.optionsTabelaPrecoCliente && clienteInfo.optionsTabelaPrecoCliente.length > 0) {
        const tabelaPrecoPrincipal = clienteInfo.optionsTabelaPrecoCliente.find(
          (tabela) => tabela.tabelapreco_id === clienteInfo.cliente.tabelaprecoid,
        );

        if (tabelaPrecoPrincipal) {
          formData.tabelaPreco = {
            tabelapreco_id: tabelaPrecoPrincipal.tabelapreco_id,
            tabelapreco_descricao: tabelaPrecoPrincipal.tabelapreco_descricao,
          };
        }
      }

      // Definir os dados no store
      store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', formData);

      // Dados completos do cliente (mesmo enriquecimento feito ao abrir o modal
      // Info Pedido / onChagenCliente). Sem isso, o perfil Cliente ficava com
      // clienteData/dataCapaPedido nulos até abrir o modal — o cálculo de
      // imposto (simulador) e o valor mínimo do pedido não funcionavam.
      if (clienteInfo && clienteInfo.cliente) {
        store.commit('InfoPedido/SET_CLIENTE_DATA', {
          clienteData: clienteInfo,
          enderecoEntrega: clienteInfo.enderecoEntrega,
          dataCapaPedido: {
            estabelecimentoid: clienteInfo.estabelecimento?.estabelecimentoid || '',
            clientenomeabreviado: clienteInfo.cliente.clientenomeabreviado,
            naturezaoperacaoid: clienteInfo.naturezaOperacao?.naturezaoperacaoid || '',
            canal_venda_id: clienteInfo.cliente.canal_venda_id,
            clienteisentoie: clienteInfo.cliente.clienteisentoie,
            tabelaprecoid: formData.tabelaPreco?.tabelapreco_id || '',
            condicaopagamentoid: formData.condicaoPagamento?.condicaopagamentoid || '',
          },
        });

        if (clienteInfo.cliente.clienteid
          && clienteInfo.estabelecimento?.estabelecimentoid
          && (formData.tabelaPreco?.tabelapreco_id || formData.condicaoPagamento?.condicaopagamentoid)) {
          store.commit('PicStore/SET_FILTERS', {
            clienteid: clienteInfo.cliente.clienteid,
            estabelecimentoid: clienteInfo.estabelecimento.estabelecimentoid,
            tabelaprecoId: formData.tabelaPreco?.tabelapreco_id || null,
            condicaopagamentoid: formData.condicaoPagamento?.condicaopagamentoid || null,
          });
        }
      }

      // Garante o tipo de pedido default (WS/primeiro permitido) ANTES de
      // persistir o cache — evita salvar tipoPedidoCliente nulo no localStorage.
      await store.dispatch('InfoPedido/garantirTipoPedidoDefault').catch(() => {});

      // Definir os campos como somente leitura
      store.commit('InfoPedido/SET_CLIENTE_READONLY', true);
      store.commit('InfoPedido/SET_REPRESENTANTE_READONLY', true);

      // Definir representante selecionado (importante para outros componentes)
      if (representante) {
        store.commit('SLSRepresentante/SET_SELECTED_REPRESENTANTE', representante);
        store.commit('intensPedido/SET_IFO_REPRESENTANTE', {
          representanteid: representante.representanteid,
          representantenome: representante.representantenome,
        });
      }

      // Salvar no localStorage com sessionId (lê o form FINAL do store, já com
      // o tipo de pedido default aplicado)
      const userId = this.getUserId();
      const sessionId = this.getSessionId();
      const formFinal = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || formData;
      const cacheData = {
        ...formFinal,
        sessionId,
        timestamp: Date.now(),
      };
      localStorage.setItem(`infoPedidoForm_${userId}`, JSON.stringify(cacheData));

      return { cliente, representante, clienteInfo };
    } catch (error) {
      console.error('Erro ao carregar dados para perfil ClienteFinal:', error);
      return null;
    }
  }

  /**
   * Repara o estado do perfil Cliente/ClienteFinal quando o carregamento do
   * login falhou ou ficou parcial (ex.: enriquecimento não-fatal falhou).
   * Sem isso, o cliente precisava abrir o modal Info Pedido para que condição
   * de pagamento/tabela/estabelecimento carregassem e o imposto fosse calculado.
   * Chamar em pontos de entrada (home/carrinho). No-op para outros perfis ou
   * quando os dados já estão completos.
   */
  static async ensureClienteFinalDataLoaded() {
    try {
      if (!isPerfilClienteAtivo()) return false;

      const form = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      const clienteData = store.getters['InfoPedido/GET_CLIENTE_DATA'];
      const completo = clienteData
        && form.cliente?.clienteid
        && form.estabelecimento?.estabelecimentoid
        && form.condicaoPagamento?.condicaopagamentoid
        && form.tabelaPreco?.tabelapreco_id;
      if (completo) return false;

      console.log('Dados do cliente incompletos — recarregando (ensureClienteFinalDataLoaded)');
      const result = await this.loadClienteFinalData();
      if (result) {
        localStorage.setItem('clienteData', JSON.stringify({
          cliente: result.cliente,
          representante: result.representante,
          detalhes: result.clienteInfo,
        }));
      }
      return !!result;
    } catch (error) {
      console.error('Erro ao garantir dados do cliente final:', error);
      return false;
    }
  }

  static async loadRepresentanteForRepresentantePerfil() {
    try {
      // Buscar o representante associado ao usuário logado.
      // Só auto-preenche quando há EXATAMENTE 1 representante — com mais de um,
      // escolher o primeiro arbitrariamente estaria errado; o usuário seleciona.
      const representantes = await slsRepresentanteService.listarRepresentanteInfoPedido();

      if (representantes && representantes.length === 1) {
        const firstRepresentante = representantes[0];

        // Setar o representante no formulário e desabilitar a edição
        store.commit('InfoPedido/SET_REPRESENTANTE_FORM', firstRepresentante);
        store.commit('SLSRepresentante/SET_SELECTED_REPRESENTANTE', firstRepresentante);
        store.commit('InfoPedido/SET_REPRESENTANTE_READONLY', true);
        store.commit('intensPedido/SET_IFO_REPRESENTANTE', {
          representanteid: firstRepresentante.representanteid,
          representantenome: firstRepresentante.representantenome,
        });

        return firstRepresentante;
      }
      return null;
    } catch (error) {
      console.error('Erro ao carregar representante para perfil Representante:', error);
      return null;
    }
  }

  static async loadRepresentantes(isClienteFinal) {
    await store.dispatch('SLSRepresentante/resertFilters');
    const representantes = await slsRepresentanteService.listarRepresentanteInfoPedido();

    // Se for ClienteFinal, retornar lista completa para verificação
    if (isClienteFinal) {
      return representantes;
    }

    return representantes;
  }

  static async selectRepresentante(representante) {
    store.commit('InfoPedido/SET_REPRESENTANTE_FORM', representante);
    store.commit('SLSRepresentante/SET_SELECTED_REPRESENTANTE', representante);
    store.commit('intensPedido/SET_IFO_REPRESENTANTE', {
      representanteid: representante.representanteid,
      representantenome: representante.representantenome,
    });

    return representante;
  }

  static async loadClientes(representanteId, isClienteFinal) {
    store.commit('SLSCliente/SET_FILTERS', {
      page: 1,
      perPage: isClienteFinal ? 100 : 1, // Se for ClienteFinal, buscar mais para contar
      representanteid: representanteId,
    });

    const clientes = await slsClienteService.listarClienteInfoPedido();

    // Se for ClienteFinal, retornar lista completa para verificação
    if (isClienteFinal) {
      return clientes;
    }

    return clientes;
  }

  static async loadClienteData(cliente) {
    try {
      const clienteDetalhado = await slsClienteService.getInfoPedidoByClienteId(cliente.clienteiderp);

      if (clienteDetalhado) {
        store.commit('InfoPedido/SET_CLIENTE_DATA', {
          clienteData: clienteDetalhado,
          enderecoEntrega: clienteDetalhado.enderecoEntrega,
          dataCapaPedido: {
            estabelecimentoid: clienteDetalhado.estabelecimento.estabelecimentoid,
            clientenomeabreviado: clienteDetalhado.cliente.clientenomeabreviado,
            naturezaoperacaoid: clienteDetalhado.naturezaOperacao?.naturezaoperacaoid || '',
            canal_venda_id: clienteDetalhado.cliente.canal_venda_id,
            clienteisentoie: clienteDetalhado.cliente.clienteisentoie,
            tabelaprecoid: clienteDetalhado.tabelaPreco.tabelapreco_id,
            condicaopagamentoid: clienteDetalhado.condicaoPagamento.condicaopagamentoid,
          },
        });

        const formInfoPedido = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'];
        store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', {
          representante: formInfoPedido.representante,
          ...clienteDetalhado,
        });

        store.commit('PicStore/SET_FILTERS', {
          clienteid: clienteDetalhado.cliente.clienteid,
          estabelecimentoid: clienteDetalhado.estabelecimento.estabelecimentoid,
          tabelaprecoId: clienteDetalhado.tabelaPreco.tabelapreco_id,
          condicaopagamentoid: clienteDetalhado.condicaoPagamento.condicaopagamentoid,
        });

        return clienteDetalhado;
      }
      return null;
    } catch (error) {
      console.error('Erro ao carregar dados do cliente:', error);
      return null;
    }
  }

  static async loadFirstRepresentante() {
    // Resetar filtros e carregar representantes
    await store.dispatch('SLSRepresentante/resertFilters');
    const representantes = await slsRepresentanteService.listarRepresentanteInfoPedido();

    if (representantes && representantes.length > 0) {
      const firstRepresentante = representantes[0];

      // Atualizar os dados do representante no store
      store.commit('InfoPedido/SET_REPRESENTANTE_FORM', firstRepresentante);
      store.commit('SLSRepresentante/SET_SELECTED_REPRESENTANTE', firstRepresentante);
      store.commit('intensPedido/SET_IFO_REPRESENTANTE', {
        representanteid: firstRepresentante.representanteid,
        representantenome: firstRepresentante.representantenome,
      });

      return firstRepresentante;
    }
    return null;
  }

  static async loadFirstCliente() {
    // Buscar o representante atual
    const { representante } = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'];
    if (!representante || !representante.representanteid) return null;

    // Definir filtros para buscar clientes associados ao representante
    store.commit('SLSCliente/SET_FILTERS', {
      page: 1,
      perPage: 1,
      representanteid: representante.representanteid,
    });

    // Buscar clientes
    const clientes = await slsClienteService.listarClienteInfoPedido();

    if (clientes && clientes.length > 0) {
      const firstCliente = clientes[0];

      // Buscar informações detalhadas do cliente
      const clienteDetalhado = await slsClienteService.getInfoPedidoByClienteId(firstCliente.clienteiderp);

      if (clienteDetalhado) {
        // Atualizar os dados do cliente no store
        store.commit('InfoPedido/SET_CLIENTE_DATA', {
          clienteData: clienteDetalhado,
          enderecoEntrega: clienteDetalhado.enderecoEntrega,
          dataCapaPedido: {
            estabelecimentoid: clienteDetalhado.estabelecimento.estabelecimentoid,
            clientenomeabreviado: clienteDetalhado.cliente.clientenomeabreviado,
            naturezaoperacaoid: clienteDetalhado.naturezaOperacao?.naturezaoperacaoid || '',
            canal_venda_id: clienteDetalhado.cliente.canal_venda_id,
            clienteisentoie: clienteDetalhado.cliente.clienteisentoie,
            tabelaprecoid: clienteDetalhado.tabelaPreco.tabelapreco_id,
            condicaopagamentoid: clienteDetalhado.condicaoPagamento.condicaopagamentoid,
          },
        });

        // Atualizar o formulário com os dados do cliente mantendo o representante atual
        const formInfoPedido = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'];
        store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', {
          representante: formInfoPedido.representante,
          ...clienteDetalhado,
        });

        // Configurar filtros para PIC (promoções)
        store.commit('PicStore/SET_FILTERS', {
          clienteid: clienteDetalhado.cliente.clienteid,
          estabelecimentoid: clienteDetalhado.estabelecimento.estabelecimentoid,
          tabelaprecoId: clienteDetalhado.tabelaPreco.tabelapreco_id,
          condicaopagamentoid: clienteDetalhado.condicaoPagamento.condicaopagamentoid,
        });

        return clienteDetalhado;
      }
    }
    return null;
  }

  static async loadCondPagamento() {
    // Buscar o representante atual
    const { representante } = store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'];
    if (!representante || !representante.representanteid) return null;

    // Buscar condições de pagamento associadas ao representante
    const condicoesPagamento = await slsCondicaoPagamentoService.GetCondPagtoByRepresentanteId(representante.representanteid);

    if (condicoesPagamento && condicoesPagamento.length > 0) {
      const firstCondPagto = {
        condicaopagamentoid: condicoesPagamento[0].condicaopagamentoid,
        condicaopagamentodescricao: condicoesPagamento[0].condicaopagamento.condicaopagamentodescricao,
      };

      // Atualizar as condições de pagamento no store
      store.commit('InfoPedido/SET_OPTIONS_COND_PAGAMENTO', condicoesPagamento);
      store.commit('InfoPedido/SET_CONDICAO_PAGAMENTO_FORM', firstCondPagto);
      store.commit('SLSCondicaoPagamento/SET_COND_PAGTOS', condicoesPagamento);

      return firstCondPagto;
    }
    return null;
  }
}
